'use client';

import { type ReactNode, useEffect, useState } from 'react';

import { DesktopSearchBar } from '#/components/DesktopSearchBar/DesktopSearchBar';
import { DesktopSearchBarLocationInput } from '#/components/DesktopSearchBar/DesktopSearchBarLocationInput';
import { DesktopSearchBarQueryInput } from '#/components/DesktopSearchBar/DesktopSearchBarQueryInput';
import { DesktopSearchBarSearchTypeSelect } from '#/components/DesktopSearchBar/DesktopSearchBarSearchTypeSelect';
import { DesktopSearchBarSubmitButton } from '#/components/DesktopSearchBar/DesktopSearchBarSubmitButton';
import {
  LocationRadiusSelect,
  type LocationRadiusSelectHandle,
} from '#/components/LocationRadiusSelect/LocationRadiusSelect';
import { RouteLoadingProgressBar } from '#/components/RouteLoadingProgressBar/RouteLoadingProgressBar';
import { FlashesContainer } from '#/containers/FlashesContainer';
import { LayoutHeaderDefaultSearchTypeContext } from '#/layouts/Layout/Header/LayoutHeaderDefaultSearchTypeContext';
import { MobileSearchV2 } from '#/layouts/Layout/Header/MobileSearchV2/MobileSearchV2';
import { useIsMobileSearchV2Enabled } from '#/layouts/Layout/Header/MobileSearchV2/useIsMobileSearchV2Enabled';
import { useLayoutHeaderSearch } from '#/layouts/Layout/Header/useLayoutHeaderSearch';
import { SearchRefinementsWrapper } from '#/modules/search/components/Refinements/SearchRefinementsWrapper';
import { useSearchRadius } from '#/modules/search/contexts/SearchContext';
import { isPointGeotype } from '#/modules/search/helpers/isPointGeotype';
import { useSearchRadiusOptions } from '#/modules/search/hooks/facets/useSearchRadiusOptions';
import { useSearchPageActions } from '#/modules/search/hooks/useSearchPageActions';
import type { SearchFromPageType } from '#/modules/search/types/SearchFromPageType';
import type { SearchType } from '#/modules/search/types/SearchType';

import {
  LayoutHeaderBottomSectionHolder,
  LayoutHeaderDesktopSearchHolder,
  LayoutHeaderProgressBarHolder,
} from './LayoutHeaderBottom.styled';

type Props = {
  defaultSearchType: SearchType;
  searchFromPageType: SearchFromPageType | undefined;
  showDesktopSearch: boolean;
  isSearchLandingPage: boolean;
  showSearchRefinements: boolean;
  searchRefinements: ReactNode;
  isSearchPage: boolean;
};

export function LayoutHeaderBottom({
  defaultSearchType,
  searchFromPageType,
  showDesktopSearch,
  isSearchLandingPage,
  showSearchRefinements,
  searchRefinements,
  isSearchPage,
}: Props) {
  const radius = useSearchRadius();
  const { setSearchUrlRadius } = useSearchPageActions({ isSearchLandingPage });

  const radiusOptions = useSearchRadiusOptions();

  const radiusHandle: LocationRadiusSelectHandle = isSearchPage
    ? { type: 'controlled', onChange: setSearchUrlRadius, radius }
    : { type: 'uncontrolled', radius };

  const isNewMobileSearch = useIsMobileSearchV2Enabled();

  const [withScrolledTransition, setWithScrolledTransition] =
    useState(isSearchPage);

  useEffect(() => {
    if (isSearchPage === withScrolledTransition) return;
    setWithScrolledTransition(isSearchPage);
  }, [isSearchPage, withScrolledTransition]);

  const {
    initialQuery,
    searchLocation,
    changeSearchLocation,
    search,
    searchOnClear,
  } = useLayoutHeaderSearch({
    defaultSearchType,
    isSearchPage,
    isSearchLandingPage,
    embedSearchWidgetId: null,
    searchFromPageType,
  });

  return (
    <LayoutHeaderBottomSectionHolder
      $isInSearchPage={isSearchPage}
      $withScrolledTransition={isSearchPage && withScrolledTransition}
    >
      {showDesktopSearch && (
        <LayoutHeaderDesktopSearchHolder data-qa-id="page-header-search-menu">
          <DesktopSearchBar
            defaultSearchType={defaultSearchType}
            onSearch={search}
            searchTypeSelect={<DesktopSearchBarSearchTypeSelect />}
            locationInput={
              <DesktopSearchBarLocationInput
                searchLocation={searchLocation ?? null}
                onChangeSearchLocation={changeSearchLocation}
              />
            }
            radiusInput={
              <LocationRadiusSelect
                handle={radiusHandle}
                options={radiusOptions}
                disabled={
                  !searchLocation || !isPointGeotype(searchLocation.geoType)
                }
                qaId="page-header-desktop-location-radius"
              />
            }
            queryInput={
              <DesktopSearchBarQueryInput
                initialQuery={initialQuery}
                searchOnClear={searchOnClear}
              />
            }
            submitButton={<DesktopSearchBarSubmitButton />}
          />
        </LayoutHeaderDesktopSearchHolder>
      )}

      {isNewMobileSearch && (
        <MobileSearchV2
          defaultSearchType={defaultSearchType}
          isSearchPage={isSearchPage}
          isSearchLandingPage={isSearchLandingPage}
          initialQuery={initialQuery}
          searchLocation={searchLocation}
          onChangeSearchLocation={changeSearchLocation}
          radiusOptions={radiusOptions}
          radiusHandle={radiusHandle}
          onSearch={search}
          searchOnClear={searchOnClear}
        />
      )}

      {showSearchRefinements && (
        <SearchRefinementsWrapper hideOnMobile={isNewMobileSearch} sticky>
          <LayoutHeaderDefaultSearchTypeContext value={defaultSearchType}>
            {searchRefinements}
          </LayoutHeaderDefaultSearchTypeContext>
        </SearchRefinementsWrapper>
      )}
      <FlashesContainer />

      <LayoutHeaderProgressBarHolder>
        <RouteLoadingProgressBar />
      </LayoutHeaderProgressBarHolder>
    </LayoutHeaderBottomSectionHolder>
  );
}
